
import { useQuery } from '@tanstack/react-query';
import { fetchAllOfficeHours } from '@/services/officeHours';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarClock, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OfficeHoursList = () => {
  const navigate = useNavigate();
  const { data: officeHours, isLoading: isLoadingOfficeHours } = useQuery({
    queryKey: ['all-office-hours'],
    queryFn: fetchAllOfficeHours
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold card-title">Scheduled Office Hours</h2>
        <Button onClick={() => navigate('/admin/office-hours/create')}>
          <Plus className="h-4 w-4 mr-2" /> Schedule Office Hours
        </Button>
      </div>
      
      <Card>
        <CardContent className="pt-6">
          {isLoadingOfficeHours ? (
            <div className="text-center py-4">Loading office hours...</div>
          ) : officeHours && officeHours.length > 0 ? (
            <div className="grid gap-4">
              {officeHours.map(session => (
                <OfficeHoursCard key={session.id} session={session} />
              ))}
            </div>
          ) : (
            <EmptyOfficeHours onCreateClick={() => navigate('/admin/office-hours/create')} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

type OfficeHoursCardProps = {
  session: any; // Replace with proper type when available
};

const OfficeHoursCard = ({ session }: OfficeHoursCardProps) => {
  const navigate = useNavigate();

  return (
    <Card key={session.id} className="overflow-hidden">
      <CardHeader className="bg-muted/50 pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg card-title">{session.title}</CardTitle>
            <CardDescription>
              {new Date(session.start_time).toLocaleDateString()} • {new Date(session.start_time).toLocaleTimeString()} - {new Date(session.end_time).toLocaleTimeString()}
            </CardDescription>
          </div>
          <CalendarClock className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex justify-between items-center">
          <div className="text-sm">
            <span className="font-medium">Mentor:</span> {session.mentor?.name || session.mentor_id}
          </div>
          <div className="text-sm">
            <span className="font-medium">Attendees:</span> {session.attendees?.length || 0}/{session.max_attendees}
          </div>
        </div>
        {session.description && (
          <div className="mt-2 text-sm text-muted-foreground">
            {session.description}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/office-hours/${session.id}`)}>
            View Details
          </Button>
          <Button variant="outline" size="sm">
            Edit
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

type EmptyOfficeHoursProps = {
  onCreateClick: () => void;
};

const EmptyOfficeHours = ({ onCreateClick }: EmptyOfficeHoursProps) => {
  return (
    <div className="text-center py-8">
      <CalendarClock className="h-12 w-12 mx-auto text-muted-foreground" />
      <h3 className="mt-4 text-lg font-medium">No office hours scheduled</h3>
      <p className="text-muted-foreground">Schedule office hours for mentors to meet with students</p>
      <Button className="mt-4" onClick={onCreateClick}>
        Schedule Office Hours
      </Button>
    </div>
  );
};

export default OfficeHoursList;
